import { Button, ButtonTheme } from 'components/Button';
import { Card } from 'components/Card';
import { CardGeneric } from 'components/CardGeneric';
import { CardRow } from 'components/CardRow';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { useAlert } from 'hooks/useAlert';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { useEsc } from 'hooks/useEsc';
import { useSelector } from 'hooks/useSelector';
import { cloneDeep, intersection, isEqual, union, without } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
  REMOTE_ASESETUP_ANSWERCLOSED,
  REMOTE_ASESETUP_ACTIONDELETE,
  REMOTE_ASESETUP_ANSWERDELETE,
  REMOTE_ASESETUP_ACTIONOPEN,
  REMOTE_ASESETUP_ANSWERSAVE,
  SET_INCOMING_VALUE,
} from 'store/actions';
import { selectIncomingValue } from 'store/app/selectors';
import {
  selectAseSetupLibraryActionIdsAlphaOrdered,
  selectAseSetupLibraryAnswer,
  selectAseSetupAnswerId,
  selectAseSetupAnswerHasActions,
} from 'store/remote/selectors';
import { AssessmentLibrary } from 'store/remote/types';
import { FindingRow } from './Rows/FindingRow';

export const AnswerScreen: React.FC<
  ModalScreenProps<ModalScreens.ASESETUPLIBRARYCHECKLISTITEM>
> = ({ route, navigation }) => {
  const dispatch = useDispatch();
  const forceUpdate = useForceUpdate();
  const alert = useAlert();
  const confirmUnsaved = useConfirmUnsaved();

  const linkActionsSelectionKey = 'AseSetupAnswerLinkActions';
  const addActionSelectionKey = 'AseSetupAnswerAddAction';

  const { questionId: inQuestionId } = route.params;

  const linkActionIds = useSelector((state) =>
    selectIncomingValue(state, linkActionsSelectionKey),
  ) as unknown as number[] | undefined;

  const addActionId = useSelector((state) =>
    selectIncomingValue(state, addActionSelectionKey),
  ) as unknown as number | undefined;

  const answerId = useSelector(selectAseSetupAnswerId);
  const initIdRef = React.useRef(answerId);

  const answer: AssessmentLibrary['answers'][number] = useSelector((state) =>
    selectAseSetupLibraryAnswer(state, answerId || -1),
  ) || {
    id: 0,
    questionId: inQuestionId!,
    answer: '',
    description: '',
    reportDescription: '',
    negative: false,
    ordering: 0,
  };

  // We need all action IDs so we can order them correctly
  const libraryActionIds = useSelector((state) =>
    selectAseSetupLibraryActionIdsAlphaOrdered(state, '_'),
  );

  const actionIds = useSelector((state) =>
    selectAseSetupAnswerHasActions(state, answer.id),
  );

  const afterCreate = React.useRef<undefined | 'addaction' | 'linkactions'>(
    undefined,
  );

  const isDirty = React.useRef(false);
  const data = React.useRef(cloneDeep(answer));
  const dataActionIds = React.useRef<number[]>(actionIds);

  // Ensure there aren't deleted actions in the list
  dataActionIds.current = intersection(dataActionIds.current, libraryActionIds);

  // Reset data if ID changes, then check isDirty
  if (data.current.id !== answer.id) {
    data.current = cloneDeep(answer);
  }

  const checkIsDirty = () =>
    !isEqual(answer, data.current) ||
    !isEqual(dataActionIds.current, actionIds);

  isDirty.current = checkIsDirty();

  const shouldReload = () => {
    if (isDirty.current !== checkIsDirty()) {
      forceUpdate();
    }
  };

  const handleDeleteAction = (actionId: number) =>
    dispatch(REMOTE_ASESETUP_ACTIONDELETE({ id: actionId }));

  const handleUnlinkAction = (actionId: number) => {
    dataActionIds.current = without(dataActionIds.current, actionId);
    forceUpdate();
  };

  const handleChangeWithName = <T extends keyof typeof data.current>(
    name: T,
    value: (typeof data.current)[T],
  ) => {
    data.current = {
      ...data.current,
      [name]: value,
    };
    shouldReload();
  };

  const handleClose = () => {
    if (isDirty.current) {
      confirmUnsaved({
        onSave: handleSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  };

  const handleSave = (close: boolean = false) => {
    if (!answer.id && data.current.answer.trim() === '') {
      alert({
        text: 'Please enter text for your answer',
      });
      return;
    }
    dispatch(
      REMOTE_ASESETUP_ANSWERSAVE({
        id: answerId,
        data: {
          ...data.current,
          actionIds: dataActionIds.current,
        },
        addData: {
          questionId: answer.questionId,
        },
        close,
      }),
    );
  };

  const handleSavePress = () => {
    if (isDirty.current || !answer.id) {
      handleSave(true);
    } else {
      dispatch(NAVIGATE_MODAL_BACK());
    }
  };

  const handleOpenAction = React.useCallback(
    (id?: number) =>
      dispatch(
        REMOTE_ASESETUP_ACTIONOPEN({
          id,
          incomingValueNewIdKey: addActionSelectionKey,
          allowLinkManagement: false,
        }),
      ),
    [dispatch],
  );

  const doAfterCreate = React.useCallback(() => {
    switch (afterCreate.current) {
      case 'addaction':
        handleOpenAction();
        break;
      case 'linkactions':
        dispatch(
          NAVIGATE_MODAL({
            screen: ModalScreens.ASESETUPLIBRARYACTIONSLIST,
            params: {
              incomingValueKey: linkActionsSelectionKey,
              hideIds: dataActionIds.current,
            },
          }),
        );
        break;
      default:
    }
  }, [dispatch, handleOpenAction]);

  const handleAddAction = (link: boolean = false) => {
    afterCreate.current = link ? 'linkactions' : 'addaction';
    if (!answer.id) {
      handleSave();
      return;
    }
    doAfterCreate();
  };

  const handleDelete = () =>
    dispatch(
      REMOTE_ASESETUP_ANSWERDELETE({
        id: answer.id,
      }),
    );

  React.useEffect(() => {
    if (linkActionIds?.length) {
      dataActionIds.current = union(dataActionIds.current, linkActionIds);
      dispatch(SET_INCOMING_VALUE({ key: linkActionsSelectionKey }));
    }
  }, [dispatch, linkActionIds]);

  React.useEffect(() => {
    if (addActionId) {
      dataActionIds.current = union(dataActionIds.current, [addActionId]);
      dispatch(SET_INCOMING_VALUE({ key: addActionSelectionKey }));
    }
  }, [dispatch, addActionId]);

  React.useEffect(() => {
    if (initIdRef.current && !answerId) {
      dispatch(NAVIGATE_MODAL_BACK());
    } else if (initIdRef.current !== answerId) {
      // Just got created
      initIdRef.current = answerId;
      doAfterCreate();
    }
  }, [answerId, dispatch, doAfterCreate]);

  React.useEffect(() => {
    return () => {
      dispatch(REMOTE_ASESETUP_ANSWERCLOSED());
    };
  }, [dispatch]);

  const isDirtyVar = isDirty.current;
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(handleClose);

  return (
    <PageScrollView
      header={
        <NavigationHeader
          heading={answerId ? 'Answer' : 'New Answer'}
          onPressCancelButton={handleClose}
          cancelButtonText="Close"
          onPressSaveButton={
            answerId || isDirty.current ? handleSavePress : null
          }
        />
      }
    >
      <Card explanation="The description is shown to assessors but doesn't appear on reports">
        <TextAreaField
          name="answer"
          onChangeWithName={handleChangeWithName}
          placeholder="Answer"
          value={data.current.answer}
          onEsc={handleClose}
          autoFocusWeb
          autoFocusNative={!data.current.id}
          autoFocusDelay
        />
        <TextAreaField
          name="description"
          onChangeWithName={handleChangeWithName}
          placeholder="Description"
          value={data.current.description}
          onEsc={handleClose}
        />
      </Card>

      <Card explanation="This report description is not shown to assessors but does appear on reports">
        <TextAreaField
          name="reportDescription"
          onChangeWithName={handleChangeWithName}
          placeholder="Report Description"
          value={data.current.reportDescription}
          onEsc={handleClose}
        />
      </Card>

      <Card
        explanation={
          'Negative answers appear in red to assessors and on reports. ' +
          'Negative answers usually require actions'
        }
      >
        <CardRow
          label="Answer Is Negative"
          checkedValue={data.current.negative}
          onPress={() => {
            data.current.negative = !data.current.negative;
            forceUpdate();
          }}
        />
      </Card>

      <Card
        label="Actions"
        labelLinkText="New"
        onLabelLinkPress={() => handleAddAction(false)}
        labelLinkText2="Link"
        onLabelLinkPress2={() => handleAddAction(true)}
      >
        {dataActionIds.current.length ? (
          libraryActionIds
            .filter((actionId) => dataActionIds.current.includes(actionId))
            .map((actionId) => (
              <FindingRow
                id={actionId}
                key={actionId}
                type="action"
                forChecklistId={answerId || -1}
                forAnswerId={0}
                forQuestionId={0}
                onPress={() => handleOpenAction(actionId)}
                inPositive
                inNegative
                onDelete={() => handleDeleteAction(actionId)}
                onUnlink={() => handleUnlinkAction(actionId)}
              />
            ))
        ) : (
          <CardRow value="No actions added yet" />
        )}
      </Card>
      {!!answer.id && (
        <CardGeneric>
          <Button
            text="Delete Answer"
            onPress={handleDelete}
            buttonTheme={ButtonTheme.themeless}
            destructive
            style={{ alignSelf: 'center' }}
          />
        </CardGeneric>
      )}
    </PageScrollView>
  );
};
